import {
  CalendarBiLogger,
  createCalendarBiLogger,
} from '../bi/biLoggerFactory';
import {
  BusinessInfo,
  BusinessInfoBase,
  Service,
} from '@wix/bookings-uou-types';
import { GetActiveFeaturesResponse } from '@wix/ambassador-services-catalog-server/types';
import {
  CalendarStateDeprecated,
  TFunction,
} from '../../components/BookingCalendar/controller';
import { CalendarApi } from '../../apiDeprecated/CalendarApi';
import { ControllerFlowAPI, ReportError } from '@wix/yoshi-flow-editor';
import Experiments from '@wix/wix-experiments';
import { Preset } from '../../types/types';
import { ISettingsParams } from '../../components/BookingCalendar/settingsParams';
import {
  getVariantByChoiceIdAndOptionId,
  isServiceVariantWithStaff,
} from '../dynamicPricing/dynamicPricing';
import { DO_NOT_CARE_STAFF } from '../../constants/constants';
import {
  getUrlQueryParamValue,
  BookingsQueryParams,
  isCartEnabled as isCartEnabledUtils,
} from '@wix/bookings-catalog-calendar-viewer-utils';
import type { CalendarSelections } from '@wix/bookings-catalog-calendar-viewer-utils';
import { SlotAvailability } from '@wix/ambassador-availability-calendar/types';

export type GetContentParams = {
  settingsParam: any;
  translationKey: string;
  options?: object;
};

export type GetContent = (params: GetContentParams) => string;

export type CalendarContext = {
  biLogger: CalendarBiLogger;
  businessInfo?: BusinessInfoBase;
  isPricingPlanInstalled: boolean;
  isMemberAreaInstalled: boolean;
  isMobile: boolean;
  activeFeatures?: GetActiveFeaturesResponse;
  calendarApi: CalendarApi;
  settings: ControllerFlowAPI['settings'];
  experiments: Experiments;
  reportError: ReportError;
  t: TFunction;
  getContent: GetContent;
  settingsParams: Record<keyof ISettingsParams, any>;
  preset: Preset;
  hasReferralParam: boolean;
  isCartEnabled: boolean;
  calendarSelections?: CalendarSelections;
  flowAPI: ControllerFlowAPI;
  isDateAndTimeViewMode: boolean;
  reloadWidget: () => Promise<void>;
  subscriptions: {
    onNextClicked?: (args: {
      service: Service;
      slotAvailability?: SlotAvailability;
    }) => Promise<{ shouldNavigate: boolean }>;
  };
};

export async function createCalendarContext({
  flowAPI,
  businessInfo,
  activeFeatures,
  calendarApi,
  initialState,
  isPricingPlanInstalled,
  isMemberAreaInstalled,
  settingsParams,
  preset,
  calendarSelections,
  reloadWidget,
  isDateAndTimeViewMode,
  subscriptions,
}: {
  flowAPI: ControllerFlowAPI;
  businessInfo?: BusinessInfo;
  activeFeatures?: GetActiveFeaturesResponse;
  calendarApi: CalendarApi;
  initialState: CalendarStateDeprecated;
  isPricingPlanInstalled: boolean;
  isMemberAreaInstalled: boolean;
  settingsParams: any;
  preset: Preset;
  calendarSelections?: CalendarSelections;
  reloadWidget: () => Promise<void>;
  isDateAndTimeViewMode: boolean;
  subscriptions: CalendarContext['subscriptions'];
}): Promise<CalendarContext> {
  const {
    settings,
    translations: { t },
    reportError,
    experiments,
    environment: { isMobile },
  } = flowAPI;
  const getContent = ({
    settingsParam,
    translationKey,
    options = {},
  }: GetContentParams) =>
    settings.get(settingsParam) || t(translationKey, options);

  const hasReferralParam = !!getUrlQueryParamValue(
    flowAPI.controllerConfig.wixCodeApi,
    BookingsQueryParams.REFERRAL,
  );

  const isCartEnabled = businessInfo
    ? await isCartEnabledUtils(
        flowAPI.controllerConfig.wixCodeApi,
        businessInfo,
      )
    : false;

  if (calendarSelections) {
    calendarSelections.services.forEach((service) => {
      const { id, resources } = service;
      const serviceVariants = initialState.serviceVariantsMap[id];

      if (
        resources &&
        resources[0] !== DO_NOT_CARE_STAFF &&
        isServiceVariantWithStaff(serviceVariants)
      ) {
        const choiceId = resources[0];
        const optionId = serviceVariants.variants?.values?.find(
          ({ choices }) => choices?.[0].staffMemberId === resources[0],
        )?.choices?.[0].optionId!;
        const selectedVariant = getVariantByChoiceIdAndOptionId(
          serviceVariants,
          choiceId,
          optionId,
        );
        service.variants = [
          {
            choices: selectedVariant!.choices,
          },
        ];
      }

      if (service.variants?.[0]) {
        service.variants[0].numberOfParticipants =
          service.variants[0].numberOfParticipants ?? 1;
      }
    });
  }

  const contextWithoutBiLogger = {
    flowAPI,
    businessInfo,
    activeFeatures,
    isPricingPlanInstalled,
    isMemberAreaInstalled,
    calendarApi,
    settings,
    experiments,
    reportError,
    t,
    getContent,
    isMobile,
    settingsParams,
    preset,
    hasReferralParam,
    isCartEnabled,
    calendarSelections,
    reloadWidget,
    isDateAndTimeViewMode,
    subscriptions,
  };

  const biLogger = createCalendarBiLogger(
    flowAPI,
    initialState,
    settings,
    settingsParams,
    preset,
    contextWithoutBiLogger,
    businessInfo,
  );

  return {
    ...contextWithoutBiLogger,
    biLogger,
  };
}
